import { Icon } from "OfficeFabric/Icon";
import { css } from "VSSUI/Util";
import * as React from "react";

import { findIndex } from "VSS/Utils/Array";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { ITableColumn } from "VSSUI/Table";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

import { GitPullRequestMergeStrategy } from "TFS/VersionControl/Contracts";
import { IMergePolicySetting } from "VersionControl/Scenarios/PullRequestDetail/Stores/ClientPolicyEvaluationStore";
import { mergeStrategyDetails } from "VersionControl/Scripts/MergeStrategyDetails";
import * as VCResources from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";
import { ISelectionRange } from "VSSUI/Utilities/Selection";

export interface IMergeStrategyDropdownProps {
    initialSelectedMergeStrategy: GitPullRequestMergeStrategy;
    onChanged(mergeStrategy: GitPullRequestMergeStrategy): void;

    canRebaseSourceBranch: boolean;
    cannotRebaseReasonHint: string;

    mergePolicySetting: IMergePolicySetting;
    canBypassPolicy: boolean;
    shouldBypassPolicy: boolean;
}

interface ICustomDropDownOption {
    key: GitPullRequestMergeStrategy;
    disabled?: boolean;
    text: string;
    subText: string;
    warningText?: string;
}

export class MergeStrategyDropdown extends React.Component<IMergeStrategyDropdownProps, {}> {
    private selection: DropdownSelection;
    private unselectableRanges: ISelectionRange[];

    constructor(props: IMergeStrategyDropdownProps) {
        super(props);

        this.selection = new DropdownSelection();
        const options = this._getOptions();

        const selectedIndex = findIndex(options, option => option.key === this.props.initialSelectedMergeStrategy);
        this.selection.select(selectedIndex);

        this.unselectableRanges = options.map((option, index) => option.disabled && { beginIndex: index, endIndex: index }).filter(x => x);
    }

    public render(): JSX.Element {
        const items = this._getOptions().map(option => {
            return { data: option, id: option.key.toString(), text: option.text };
        });

        return <>
            <div className="section-header">{VCResources.MergeType}</div>
            <Dropdown<ICustomDropDownOption>
                ariaLabel={VCResources.MergeType}
                selection={this.selection}
                onSelect={this._onChanged}
                items={items}
                renderItem={this._onRenderOption}
                getUnselectableRanges={this.getUnselectableRanges}
            />
        </>;
    }

    private _getOptions(): ICustomDropDownOption[] {

        let result: ICustomDropDownOption[] = [];

        for (let strategy in mergeStrategyDetails) {
            const option = mergeStrategyDetails[strategy];

            const allowedByPolicy = option.isAllowedByPolicy(this.props.mergePolicySetting);

            if (option.isRebase && !this.props.canRebaseSourceBranch) {
                // Not allowed under any circumstances, bypass doesn't matter.
                result.push({
                    ...option,
                    disabled: true,
                    warningText: this.props.cannotRebaseReasonHint,
                } as ICustomDropDownOption);
            }
            else if (allowedByPolicy) {
                // Merge type is allowed and policy permits it
                result.push(option);
            }
            else if (this.props.canBypassPolicy) {
                // Policy prohibits this strategy, but user has permission to bypass policies. Clicking the button should select
                // the merge strategy, enable the bypass option, and move focus to bypass reason.
                result.push({
                    ...option,
                    warningText: VCResources.PullRequest_CompleteMergeDropdown_RequiresBypass,
                } as ICustomDropDownOption);
            }
            else {
                // Policy prohibits this strategy and user cannot bypass.
                result.push({
                    ...option,
                    disabled: true,
                    warningText: VCResources.PullRequest_CompleteMergeDropdown_PoliciesProhibit,
                } as ICustomDropDownOption);
            }
        }

        return result;
    }

    private _onRenderOption = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IListBoxItem<ICustomDropDownOption>>,
        tableItem: IListBoxItem<ICustomDropDownOption>,
    ): JSX.Element => {
        const option = tableItem.data;
        return (
            <td
                key={option.key}
                className={css(
                    "vc-completion-type-row",
                    (option.disabled && "vc-completion-type-disabled"),
                )}
            >
                <div className="vc-completion-type-checkmark">
                    {option.key == this.props.initialSelectedMergeStrategy &&
                        <Icon iconName="CheckMark" />
                    }
                </div>
                <div className="vc-completion-type-choice">
                    <div key="t" className="vc-completion-type-text">{option.text}</div>
                    <div key="s" className="vc-completion-type-subtext">{option.subText}</div>
                    {option.warningText &&
                        <div key="e" className={css(
                            "vc-completion-type-subtext",
                            "vc-completion-type-warning-text",
                            !option.disabled && "can-bypass",
                        )}>
                            {option.warningText}
                        </div>
                    }
                </div>
            </td>
        );
    }

    private _onChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<ICustomDropDownOption>): void => {
        if (this.props.onChanged) {
            this.props.onChanged(item.data.key as GitPullRequestMergeStrategy);
        }
    }

    private getUnselectableRanges = () => this.unselectableRanges;
}